import { useQuery } from '@tanstack/react-query';
import { parse } from 'tldts';

import { api } from '@services/apiService';

export function useFavicon(domain: string) {
  return useQuery({
    queryKey: ['favicons', domain],
    queryFn: async () => {
      return (await getFavicon(domain)) || null;
    },
    refetchOnMount: false,
  });
}

async function fetchFavicon(domain: string) {
  const parsed = parse(domain);
  let apexDomain = parsed.domain;
  if (!apexDomain) {
    throw new Error('Invalid domain');
  }

  if (domain.startsWith('*.')) {
    apexDomain = domain.substring(2);
  }

  // Use Google favicon for GTM
  if (apexDomain === 'googletagmanager.com') {
    apexDomain = 'google.com';
  }

  return await api.get(`favicons/${apexDomain}`);
}

// Return the favicon for a given domain as a base64 encoded string. If the favicon
// cannot be fetched, return undefined.
function getFavicon(domain: string): Promise<string | undefined> {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    try {
      const response = await fetchFavicon(domain);
      if (!response.ok) {
        resolve(undefined);
      }

      const blob = await response.blob();

      const reader = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result?.toString());
      };

      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(blob);
    } catch {
      resolve(undefined);
    }
  });
}
